import { findPpxTests, isAlcotestMain, testName } from "@codeshare/runner-ocaml";

/** Something runnable in a file, for the ▷ Run / ▷ Run test links above it. */
export interface Runnable {
  kind: "main" | "test";
  /** The test's name as its runner reports it ("TestAdd", "Calc::test_add", "math > adds"). */
  name: string;
  /** 1-based line of the declaration. */
  line: number;
}

const TEST_FILES: Record<string, RegExp> = {
  go: /_test\.go$/,
  python: /(^|\/)(test_[^/]*|[^/]*_test)\.py$/,
  javascript: /\.(test|spec)\.[cm]?[jt]sx?$/,
  typescript: /\.(test|spec)\.[cm]?[jt]sx?$/,
  tsx: /\.(test|spec)\.[cm]?[jt]sx?$/,
  ruby: /(^|\/)(test_[^/]*|[^/]*_test)\.rb$/,
  ocaml: /(^|\/)(tests?\/[^/]*|test_[^/]*|[^/]*_tests?|tests?)\.ml$/,
};

/**
 * Whether a path is a test file for the language's runner. OCaml tests can be in any file
 * (inline and expect tests live next to the code): with its source, a file that has some is one.
 */
export function isTestFile(languageId: string, path: string, source?: string): boolean {
  if (TEST_FILES[languageId]?.test(path)) return true;
  return (
    languageId === "ocaml" &&
    source !== undefined &&
    (findPpxTests(source).length > 0 || isAlcotestMain(source))
  );
}

const indentOf = (line: string) => line.length - line.trimStart().length;

/** The program's entry and each test in a file, by language (a light scan, not a parser). */
export function findRunnables(languageId: string, path: string, source: string): Runnable[] {
  const lines = source.split("\n");
  const found: Runnable[] = [];
  const testFile = isTestFile(languageId, path, source);
  if (languageId === "go") {
    lines.forEach((text, i) => {
      if (!testFile && /^func\s+main\s*\(\s*\)/.test(text))
        found.push({ kind: "main", name: "main", line: i + 1 });
      const test = /^func\s+(Test(?:[^a-z\W]\w*)?)\s*\(\s*\w+\s+\*testing\.T\s*\)/.exec(text);
      if (testFile && test) found.push({ kind: "test", name: test[1] as string, line: i + 1 });
    });
  } else if (languageId === "python") {
    let cls: { name: string; indent: number } | undefined;
    lines.forEach((text, i) => {
      if (!testFile && /^if\s+__name__\s*==\s*["']__main__["']\s*:/.test(text))
        found.push({ kind: "main", name: "main", line: i + 1 });
      if (!testFile || !text.trim()) return;
      const indent = indentOf(text);
      if (cls && indent <= cls.indent) cls = undefined;
      const klass = /^(\s*)class\s+(\w+)/.exec(text);
      if (klass) {
        cls = { name: klass[2] as string, indent };
        return;
      }
      const def = /^\s*(?:async\s+)?def\s+(test\w*)\s*\(/.exec(text);
      if (def && (indent === 0 || cls))
        found.push({
          kind: "test",
          name: cls && indent > cls.indent ? `${cls.name}::${def[1]}` : (def[1] as string),
          line: i + 1,
        });
    });
  } else if (languageId === "javascript" || languageId === "typescript" || languageId === "tsx") {
    if (!testFile) return found;
    // describe(...) blocks by indentation: tests inside take their names as a prefix.
    const suites: { name: string; indent: number }[] = [];
    lines.forEach((text, i) => {
      const indent = indentOf(text);
      while (suites.length > 0 && text.trim() && indent <= (suites.at(-1)?.indent ?? -1))
        suites.pop();
      const call =
        /^\s*(describe|suite|test|it)(?:\.(?:only|skip|todo|concurrent))?\s*\(\s*(["'`])((?:\\.|(?!\2).)*)\2/.exec(
          text,
        );
      if (!call) return;
      const name = (call[3] as string).replace(/\\(.)/g, "$1");
      if (call[1] === "describe" || call[1] === "suite") suites.push({ name, indent });
      else
        found.push({
          kind: "test",
          name: [...suites.map((s) => s.name), name].join(" > "),
          line: i + 1,
        });
    });
  } else if (languageId === "ocaml") {
    // An Alcotest executable runs its suites; otherwise the program is its top-level `let () =`.
    const alcotest = lines.findIndex((text) => /\bAlcotest\.run\b/.test(text));
    if (alcotest >= 0) found.push({ kind: "test", name: "", line: alcotest + 1 });
    else {
      const main = lines.findIndex((text) => /^let\s+\(\)\s*=/.test(text));
      if (main >= 0 && findPpxTests(source).length === 0)
        found.push({ kind: "main", name: "main", line: main + 1 });
    }
    for (const test of findPpxTests(source))
      found.push({ kind: "test", name: testName(test), line: test.line });
  } else if (languageId === "ruby") {
    if (!testFile) return found;
    let cls: string | undefined;
    lines.forEach((text, i) => {
      const klass = /^\s*class\s+([\w:]+)/.exec(text);
      if (klass) cls = klass[1];
      const def = /^\s*def\s+(test_\w+)/.exec(text);
      if (def && cls) found.push({ kind: "test", name: `${cls}#${def[1]}`, line: i + 1 });
    });
  }
  return found;
}
